import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useReactToPrint } from "react-to-print"
import { useDispatch, useSelector } from "react-redux"
import { usePermissionsList } from "../../../hooks/queries/usePermissions"
import { BsPrinter } from "react-icons/bs"
import { useAuth0 } from "@auth0/auth0-react"
import { usePostHog } from "posthog-js/react"
import { FaCircleExclamation } from "react-icons/fa6"
import _ from "lodash"

import NurseListContainer from "../../NurseAssignments/NurseListView/NurseListContainer"
import SubmitScheduleButton from "./SubmitScheduleButton"
import ChairListView from "../../NurseAssignments/ChairListView"
import CustomGanttNurse from "../../NurseAssignments/CustomGanttNurse"
import CustomGanttChair from "../../NurseAssignments/CustomGanttChair"
import GridView from "../GridView"
import NurseTable, { prepareDataForTable } from "../NurseTable"
import { defaultShifts } from "../StaffingEntryTable"
import AddNursePopup from "./AddNursePopup/AddNursePopup"
import RemoveNursePopup from "./RemoveNursePopup"
import { useSchedulePatch } from "../../../hooks/useSchedulePatch"
import { PatientSchema } from "../../../features/schedule/ScheduleSchema"
import CancelledPatientsPopup from "../CancelledPatients"
import {
	addPatientToEditedPatients,
	transferPatientsBetweenNursesAndChairs,
	setPatientsCancellationConsideredForReassignment,
	setShownCancelledPatients
} from "../../../features/schedule/scheduleSlice" // Update with the correct path to your slice

import { setTomorrowMode } from "../../../features/schedule/tomorrowSlice"
import useFetchSchedules from "../../../hooks/useFetchSchedules/useFetchSchedules"
import useFetchSchedulesTomorrow from "../../../hooks/useFetchSchedules/useFetchSchedulesTomorrow"
import useFetchSettings from "../../../hooks/useFetchSettings"
import EditPatient from "../../NurseAssignments/EditPatient"
import PatientReassignmentConfirmation from "./PatientReassignmentConfirmation"

import { getCurrentHours } from "../../../utils/generalUtils"
import {
	getDateFormatted,
	getFirstOpenDateToday,
	getFirstOpenDateTomorrow,
	getDateToday,
	getClinicTimes
} from "../../../utils/generalUtils"

import { usePatientReassignment } from "../../../hooks/usePatientReassignments"
import {
	confirmedCancellationNoShow,
	confirmedCompletedAllCases,
	getCancelledNoShowPatientsNotShown
} from "../../../utils/realTimeUtils"

import {
	getScheduleSelection,
	useScheduleForSelection
} from "./scheduleSelection"
import ReassignmentsErrorPopup from "./ReassignmentsErrorPopup"
import FutureStaffingButton from "./FutureStaffingButton"
import { PageLoader } from "../../Authentication/page-loader"
import { setSandboxMode } from "../../../features/schedule/sandboxSlice"
import ExtraRoleInfo from "./ExtraRoleInfo"
import CurrentModeDialogue from "./CurrentModeDialogue"
import useLogging from "../../../hooks/useLogging"
import useFetchFilteredPatients from "../../../hooks/useFetchFilteredPatients"
import FilteredPatientsContainer from "../FilteredPatientsContainer"

function canViewTomorrowSchedule(currentHour, hasWriteSchedulesPermission, permissions) {
	// Tomorrow's schedule is generated at 11 AM, so it can be viewed after noon.
	const viewThreshold = 12 // 12 PM

	return (
		(currentHour >= viewThreshold && hasWriteSchedulesPermission) ||
		permissions?.includes("write:admin")
	)
}

const CustomCalendar = ({ setNotification, nurseName, nurseStaffing }) => {
	const { user } = useAuth0()
	const posthog = usePostHog()
	const dispatch = useDispatch()
	const logging = useLogging()
	const [value, setValue] = useState(localStorage.getItem("selectedView") || "1")
	const [btnSelect, setBtnSelect] = useState(() => {
		// If nurseStaffing is false, always default to "Nurse Schedule"
		if (!nurseStaffing) {
			return "Nurse Schedule"
		}
		// Otherwise use localStorage value or default to "Nurse Schedule"
		return localStorage.getItem("selectedSchedule") || "Nurse Schedule"
	})

	const [manualRefreshKey, setManualRefreshKey] = useState(0)
	const { getAccessTokenSilently } = useAuth0()

	const printRef = useRef(null)
	const printFn = useReactToPrint({ contentRef: printRef })

	useFetchSchedules("CustomCalendar")
	useFetchSchedulesTomorrow("CustomCalendar")
	const { settingsData } = useFetchSettings()

	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)
	const schedulePatch = useSchedulePatch({ isTomorrowMode, sandbox })

	const scheduleSelection = getScheduleSelection(isTomorrowMode, sandbox)
	const {
		reassignmentsForAddOnPatients,
		unassignedPatientsForAddOnPatients,
		reassignmentsForRemovedPatients,
		removedPatientsToConsider,
		error: reassignmentsError
	} = usePatientReassignment(sandbox)
	const [isAddOnReassignmentOpen, setIsAddOnReassignmentOpen] = useState(false)
	const [isRemovedReassignmentOpen, setIsRemovedReassignmentOpen] = useState(false)

	const currentSchedule = useScheduleForSelection(scheduleSelection)

	const patientSchedule = currentSchedule.patientSchedule
	const nurseSchedule = currentSchedule.nurseSchedule
	const chairSchedule = currentSchedule.chairSchedule
	const chargeNurse = currentSchedule.chargeNurse

	// Get tomorrowNurses from Redux store
	const tomorrowNurses = useSelector(state => state.tomorrow.nurseSchedule)
	const tomorrowChargeNurse = useSelector(state => state.tomorrow.chargeNurse)

	// Combine charge nurse and regular nurses for the table
	const combinedTomorrowNurses = useMemo(() => {
		if (!tomorrowChargeNurse || !tomorrowNurses) return []
		const combinedData = [tomorrowChargeNurse, ...tomorrowNurses]
		return prepareDataForTable(combinedData, defaultShifts)
	}, [tomorrowChargeNurse, tomorrowNurses])

	const isLocked = useSelector(state => state.schedule.isLocked)

	const [selectedTasks, setSelectedTasks] = useState([])

	const permissions = usePermissionsList()

	const [hasWriteSchedulesPermission, setHasWriteSchedulesPermission] = useState(false)

	// NOTE: This is for the nurse table
	const [editsMade, setEditsMade] = useState(false)
	const [tableNurses, setTableNurses] = useState(combinedTomorrowNurses)
	const [selectedNurseName, setSelectedNurseName] = useState("")
	const [selectedRole, setSelectedRole] = useState("")
	const [selectedShiftStart, setSelectedShiftStart] = useState("")
	const [selectedShiftEnd, setSelectedShiftEnd] = useState("")
	const [selectedShift, setSelectedShift] = useState("Custom Shift")
	const [deallocatedPatients] = useState([])
	const [transferredPatients] = useState([])

	// Update tableNurses when combinedTomorrowNurses changes
	useEffect(() => {
		setTableNurses(combinedTomorrowNurses)
	}, [combinedTomorrowNurses])

	// Reset btnSelect to "Nurse Schedule" when switching to tomorrow's mode while on Future Staffing
	useEffect(() => {
		if (isTomorrowMode && btnSelect === "Future Staffing") {
			setBtnSelect("Nurse Schedule")
			localStorage.setItem("selectedSchedule", "Nurse Schedule")
		}
	}, [isTomorrowMode, btnSelect])

	// Add state for cancelled patients popup
	const [isCancelledPatientsPopupOpen, setIsCancelledPatientsPopupOpen] = useState(false)

	useEffect(() => {
		// Set hasWriteSchedulesPermission based on initial permissions
		if (permissions && permissions.includes("write:schedules")) {
			setHasWriteSchedulesPermission(true)
		}
	}, [permissions])

	// Check for unshown cancelled patients
	const notShownCancelledPatients = getCancelledNoShowPatientsNotShown(patientSchedule)

	useEffect(() => {
		if (isTomorrowMode) {
			setIsAddOnReassignmentOpen(false)
			setIsRemovedReassignmentOpen(false)
			return
		}
		if (sandbox) {
			setIsAddOnReassignmentOpen(false)
			setIsRemovedReassignmentOpen(false)
			return
		}

		if (permissions && !permissions.includes("write:schedules")) {
			setIsAddOnReassignmentOpen(false)
			setIsRemovedReassignmentOpen(false)
			return
		}

		if (selectedTasks.length > 0) {
			//there an edit patient window open
			setIsAddOnReassignmentOpen(false)
			setIsRemovedReassignmentOpen(false)
			return
		}

		// If there are any reassignments after fetching the schedules, prompt the user.
		if (reassignmentsForAddOnPatients && reassignmentsForAddOnPatients.length > 0) {
			setIsAddOnReassignmentOpen(true)
			setIsRemovedReassignmentOpen(false)
		} else {
			setIsAddOnReassignmentOpen(false)
			if (reassignmentsForRemovedPatients && reassignmentsForRemovedPatients.length > 0) {
				setIsRemovedReassignmentOpen(true)
			} else {
				setIsRemovedReassignmentOpen(false)
			}
		}

		if (notShownCancelledPatients.length > 0) {
			setIsCancelledPatientsPopupOpen(true)
		}
		// `notShownCancelledPatients.length` and `permissions` are read at effect-run time and intentionally omitted to avoid re-triggering popups.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		reassignmentsForAddOnPatients,
		reassignmentsForRemovedPatients,
		sandbox,
		isTomorrowMode,
		selectedTasks,
		patientSchedule
	])

	// Handle acceptance of reassignment
	const handleAddOnReassignmentAccept = async () => {
		posthog.capture("Add On Reassignments Accepted")
		logging.info("Add On Reassignments Accepted")
		setIsAddOnReassignmentOpen(false)

		dispatch(transferPatientsBetweenNursesAndChairs(reassignmentsForAddOnPatients))

		if (!sandbox && !isTomorrowMode) {
			// Now "Push" the changes to the backend

			await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			await schedulePatch.mutateAsync({
				date: getDateFormatted(
					getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)
				),
				setNotification
			})
		}

		// After execution, final schedules have been dispatched inside executeReassignment
		// If needed, you can set notifications or handle further logic here.
		setNotification({
			visible: true,
			message: "Reassignments accepted and applied.",
			success: true
		})
		setTimeout(() => {
			setNotification(prev => ({ ...prev, visible: false }))
		}, 2000)
	}

	const handleRemovedReassignmentAccept = async () => {
		posthog.capture("Removed Reassignments Accepted")
		setIsRemovedReassignmentOpen(false)

		dispatch(transferPatientsBetweenNursesAndChairs(reassignmentsForRemovedPatients))
		dispatch(
			setPatientsCancellationConsideredForReassignment({
				patientIds: removedPatientsToConsider.map(patient => patient.patientId)
			})
		)

		if (!sandbox && !isTomorrowMode) {
			// Now "Push" the changes to the backend

			await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			await schedulePatch.mutateAsync({
				date: getDateFormatted(
					getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)
				),
				setNotification
			})
		}

		setNotification({
			visible: true,
			message: "Reassignments accepted and applied.",
			success: true
		})
		setTimeout(() => {
			setNotification(prev => ({ ...prev, visible: false }))
		}, 2000)
	}

	const handleRemovedReassignmentDecline = async () => {
		posthog.capture("Removed Reassignments Declined")
		setIsRemovedReassignmentOpen(false)
		dispatch(
			setPatientsCancellationConsideredForReassignment({
				patientIds: removedPatientsToConsider.map(patient => patient.patientId)
			})
		)

		await getAccessTokenSilently({
			audience: process.env.REACT_APP_API_IDENTIFIER
		})

		await schedulePatch.mutateAsync({
			date: getDateFormatted(
				getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)
			),
			setNotification
		})
	}

	// Handle declining of reassignment

	const handleAddOnReassignmentDecline = async () => {
		posthog.capture("Add On Reassignments Declined")
		setIsAddOnReassignmentOpen(false)

		const tasksToAdd = []

		for (const patient of unassignedPatientsForAddOnPatients) {
			const { patient: parsedPatient, error } = PatientSchema.safeParse(_.cloneDeep(patient))
			if (error) {
				logging.error("error in handleAddOnReassignmentDecline", error)
				//something is very wrong here
				if (patient.patientId) {
					//try to find the patient in the patientSchedule
					const foundPatient = _.find(patientSchedule, { patientId: patient.patientId })
					if (foundPatient) {
						tasksToAdd.push(foundPatient)
						continue
					}
				}
				//if we're here there was no patientId or we couldn't find the patient in the patientSchedule
				logging.error("patient not found in handleAddOnReassignmentDecline", patient)
				//we're skipping this patient here
				continue
			} else {
				tasksToAdd.push(parsedPatient)
			}
		}
		setSelectedTasks(tasksToAdd)

		// Declining means the user wants to manually fix assignments.
		// For each patient that was supposed to be reassigned, show the EditPatient popup.
		// You may have multiple patients. You can handle them one at a time. For simplicity,
		// let's assume you pick the first patient to edit:

		if (unassignedPatientsForAddOnPatients.length > 0) {
			// Start editing the first patient
			//replace the assigned nurse and assigned chair in the unassigned patients so the default
			//values are filled in
			setSelectedTasks(unassignedPatientsForAddOnPatients)
			// TODO: reassignmentsForAddOnPatients, reassignmentsForRemovedPatients could be chnaged between setting false and this
		} else {
			setNotification({
				visible: true,

				message: "No reassignments to process.",
				success: true
			})
			setTimeout(() => {
				setNotification(prev => ({ ...prev, visible: false }))
			}, 2000)
		}
	}

	const handleCloseEditPatientPopup = async () => {
		posthog.capture("Edit (Add On) Patient Popup Closed")
		const numberOfTasks = selectedTasks.length

		if (numberOfTasks === 0) {
			return
		}

		const selectedTask = selectedTasks[0]

		dispatch(addPatientToEditedPatients(selectedTask))

		//pop the first task from the array
		setSelectedTasks(selectedTasks.slice(1))

		// If there are more unassigned patients after the current one, show the next one

		if (numberOfTasks === 1) {
			setNotification({
				visible: true,
				message: "All unassigned patients have been processed.",
				success: true
			})
		}
	}

	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []

	// Calculate open and close times using date-aware methods
	const { openTime, closeTime } = useMemo(() => {
		// Determine which date to use based on the current mode
		const currentDate = isTomorrowMode
			? getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
			: getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)

		const { openTime: openTimeMinutes, closeTime: closeTimeMinutes } = getClinicTimes(
			settingsData,
			currentDate
		)

		return {
			openTime: openTimeMinutes,
			closeTime: closeTimeMinutes
		}
		// centerClosedDates is stable in practice (read from settings); wrapping each call site adds noise.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTomorrowMode, centerOpen, testEnvironment, centerClosedDates, settingsData])

	const [currentHour, setCurrentHour] = useState(getCurrentHours())

	useEffect(() => {
		const updateCurrentHour = () => {
			setCurrentHour(getCurrentHours())
		}

		// Update the current hour immediately when the component mounts
		updateCurrentHour()

		// Set up interval to update the current hour every 15 minutes
		const intervalId = setInterval(updateCurrentHour, 900000)

		// Clean up the interval on component unmount
		return () => clearInterval(intervalId)
	}, [])

	const handleChartRerender = useCallback(() => {
		// This function is only called for manual refreshes
		setManualRefreshKey(prev => prev + 1)
	}, [])

	let { filteredPatients } = useFetchFilteredPatients(
		getDateFormatted(getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)),
		currentSchedule.documentId
	)

	if (
		!settingsData ||
		!patientSchedule ||
		!nurseSchedule ||
		!chairSchedule ||
		patientSchedule.length === 0 ||
		nurseSchedule.length === 0 ||
		chairSchedule.length === 0 ||
		!filteredPatients
	) {
		return <PageLoader />
	}

	const handlePrint = async () => {
		printFn()
	}

	const handleViewTomorrowSchedule = async () => {
		dispatch(setTomorrowMode(true))
		dispatch(setSandboxMode(false))
	}

	const handleViewChange = newValue => {
		setValue(newValue)
		localStorage.setItem("selectedView", newValue)
	}

	const handleScheduleChange = newSchedule => {
		setBtnSelect(newSchedule)
		localStorage.setItem("selectedSchedule", newSchedule)
	}

	const unassignedPatients = patientSchedule.filter(
		patient =>
			!patient.assignedNurse &&
			!confirmedCancellationNoShow(patient) &&
			!confirmedCompletedAllCases(patient, sandbox, isTomorrowMode)
	)

	function onShowCancelledPatientsPopupClose() {
		setIsCancelledPatientsPopupOpen(false)
		dispatch(
			setShownCancelledPatients({
				patientIds: notShownCancelledPatients.map(patient => patient.patientId)
			})
		)
	}

	const todayShown = _.isEqual(
		getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates),
		getDateToday()
	)

	return (
		<>
			{/* Display Current Mode */}
			<CurrentModeDialogue />

			{/* Filtered Patients Box */}
			<FilteredPatientsContainer filteredPatients={filteredPatients} />

			{/* Warning Box for Unassigned Patients */}
			{unassignedPatients.length > 0 && todayShown && hasWriteSchedulesPermission && (
				<div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 my-4 overflow-y-auto">
					<p className="font-bold mt-4 flex items-center justify-center">
						<FaCircleExclamation
							aria-hidden="true"
							className="h-5 w-5 text-red-500 mr-2"
						/>
						<span className="text-center">Warning: Unassigned Patients</span>
					</p>
					<p className="mb-8">
						New patients were added to the schedule. Please assign them to a team
						member.
					</p>
					<NurseListContainer
						nurseId={null}
						setNotification={setNotification}
						isToday={todayShown}
					/>
				</div>
			)}

			<div className="flex justify-between items-center mb-10 overflow-x-auto">
				<div className="flex flex-wrap items-center space-x-3">
					<button
						className={`px-4 py-2 rounded-md flex items-center ${
							btnSelect === "Nurse Schedule"
								? "text-white bg-customBlue"
								: "text-black bg-gray-100 hover:bg-gray-200"
						}`}
						onClick={() => handleScheduleChange("Nurse Schedule")}
					>
						Nurse Schedule
					</button>
					<button
						className={`px-4 py-2 rounded-md items-center ${
							btnSelect === "Chair Schedule"
								? "text-white bg-customBlue"
								: "text-black bg-gray-100 hover:bg-gray-200"
						}`}
						onClick={() => handleScheduleChange("Chair Schedule")}
					>
						Chair Schedule
					</button>
					<button
						className={`px-4 py-2 rounded-md items-center ${
							btnSelect === "Grid View"
								? "text-white bg-customBlue"
								: "text-black bg-gray-100 hover:bg-gray-200"
						}`}
						onClick={() => handleScheduleChange("Grid View")}
					>
						Grid View
					</button>
					{nurseStaffing &&
						hasWriteSchedulesPermission &&
						!isTomorrowMode &&
						!sandbox && (
							<FutureStaffingButton
								btnSelect={btnSelect}
								onClick={() => handleScheduleChange("Future Staffing")}
							/>
						)}
				</div>
				<div className="flex flex-wrap items-center space-x-3">
					<SubmitScheduleButton
						isLocked={isLocked}
						hasWriteSchedulesPermission={hasWriteSchedulesPermission}
						setNotification={setNotification}
						isTomorrowMode={isTomorrowMode}
						setBtnSelect={setBtnSelect}
						tableNurses={tableNurses}
						deallocatedPatients={deallocatedPatients}
						transferredPatients={transferredPatients}
						setEditsMade={setEditsMade}
						selectedNurseName={selectedNurseName}
						selectedRole={selectedRole}
						selectedShiftStart={selectedShiftStart}
						selectedShiftEnd={selectedShiftEnd}
					/>
					{btnSelect !== "Grid View" && (
						<button
							className="px-4 py-2 rounded-md hover:bg-customBlue-dark flex items-center text-white bg-customBlue"
							onClick={handlePrint}
						>
							<BsPrinter className="mr-2" />
							Print
						</button>
					)}
				</div>
			</div>
			{!sandbox &&
				canViewTomorrowSchedule(
					currentHour,
					hasWriteSchedulesPermission,
					permissions
				) && (
					<div className="flex items-center justify-end space-x-4">
						{isTomorrowMode ? (
							<button
								onClick={() => {
									dispatch(setTomorrowMode(false))
									setNotification({
										visible: true,
										message: "Viewing today's schedule",
										success: true
									})
									setTimeout(() => {
										setNotification({ visible: false, message: "" })
									}, 2000)
								}}
								className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 flex items-center"
							>
								<span className="animate-pulse mr-2 h-3 w-3 rounded-full bg-green-500"></span>
								Back to Today's Schedule
							</button>
						) : (
							canViewTomorrowSchedule(
								currentHour,
								hasWriteSchedulesPermission,
								permissions
							) && (
								<button
									onClick={async () => {
										await handleViewTomorrowSchedule()
										setNotification({
											visible: true,
											message: "Viewing tomorrow's schedule",
											success: true
										})
										setTimeout(() => {
											setNotification({ visible: false, message: "" })
										}, 2000)
									}}
									className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 flex items-center"
								>
									<span className="animate-pulse mr-2 h-3 w-3 rounded-full bg-green-500"></span>
									Review Tomorrow's Schedule
								</button>
							)
						)}
					</div>
				)}
			<div ref={printRef} id="print-section">
				<div className="w-full">
					{btnSelect !== "Grid View" && btnSelect !== "Future Staffing" && (
						<div className="flex">
							<button
								className={`text-sm font-medium px-4 py-2 ${
									value === "1"
										? "border-b-2 border-custom-blue"
										: "hover:text-customBlue"
								}`}
								onClick={() => handleViewChange("1")}
							>
								Gantt Chart
							</button>
							<button
								className={`text-sm font-medium px-4 py-2 ${
									value === "2"
										? "border-b-2 border-custom-blue"
										: "hover:text-customBlue"
								}`}
								onClick={() => handleViewChange("2")}
							>
								List View
							</button>
						</div>
					)}

					{btnSelect === "Grid View" ? (
						<GridView
							patientSchedule={patientSchedule}
							nurseSchedule={nurseSchedule}
							chairSchedule={chairSchedule}
							chargeNurse={chargeNurse}
							setNotification={setNotification}
							openTime={openTime}
							closeTime={closeTime}
							settingsData={settingsData}
						/>
					) : btnSelect === "Future Staffing" && !sandbox && !isTomorrowMode ? (
						<NurseTable
							setNotification={setNotification}
							selectedNurseName={selectedNurseName}
							selectedRole={selectedRole}
							selectedShiftStart={selectedShiftStart}
							selectedShiftEnd={selectedShiftEnd}
							selectedShift={selectedShift}
							setSelectedNurseName={setSelectedNurseName}
							setSelectedRole={setSelectedRole}
							setSelectedShiftStart={setSelectedShiftStart}
							setSelectedShiftEnd={setSelectedShiftEnd}
							setSelectedShift={setSelectedShift}
							editsMade={editsMade}
							setEditsMade={setEditsMade}
							tableNurses={tableNurses}
							setTableNurses={setTableNurses}
						/>
					) : (
						<>
							{value === "1" && (
								<div className="bg-white rounded-xl p-6">
									{btnSelect === "Chair Schedule" && (
										<CustomGanttChair
											key={`chair-${manualRefreshKey}`}
											setNotification={setNotification}
											handleChartRerender={handleChartRerender}
											patientSchedule={patientSchedule}
											nurseSchedule={nurseSchedule}
											chairSchedule={chairSchedule}
											openTime={openTime}
											closeTime={closeTime}
											isTomorrowMode={isTomorrowMode}
											documentId={currentSchedule.documentId}
										/>
									)}
									{btnSelect === "Nurse Schedule" && (
										<CustomGanttNurse
											key={`nurse-${manualRefreshKey}`}
											setNotification={setNotification}
											handleChartRerender={handleChartRerender}
											nurseName={nurseName}
											patientSchedule={patientSchedule}
											nurseSchedule={nurseSchedule}
											chairSchedule={chairSchedule}
											openTime={openTime}
											closeTime={closeTime}
											isTomorrowMode={isTomorrowMode}
											documentId={currentSchedule.documentId}
										/>
									)}
								</div>
							)}

							{value === "2" && (
								<div key={manualRefreshKey} className="bg-white rounded-xl py-6">
									{btnSelect === "Nurse Schedule" && (
										<>
											<NurseListContainer
												setNotification={setNotification}
												context="icTracker"
												isToday={todayShown}
											/>
										</>
									)}

									{btnSelect === "Chair Schedule" && (
										<>
											<ChairListView
												isTomorrowMode={isTomorrowMode}
												setNotification={setNotification}
												context="icTracker"
												isToday={todayShown}
											/>
										</>
									)}
								</div>
							)}
						</>
					)}

					{((btnSelect === "Nurse Schedule" && value === "2") ||
						(btnSelect === "Chair Schedule" && value === "2") ||
						btnSelect === "Grid View") && (
						<div className="flex flex-col gap-4">
							<div className="flex justify-end">
								<div className="flex flex-wrap items-center gap-2">
									<AddNursePopup
										setNotification={setNotification}
										onClose={handleChartRerender}
										isTomorrowMode={isTomorrowMode}
									/>
									<RemoveNursePopup
										setNotification={setNotification}
										onClose={handleChartRerender}
										isTomorrowMode={isTomorrowMode}
									/>
								</div>
							</div>
						</div>
					)}

					{btnSelect !== "Future Staffing" && (
						<div className="flex flex-col gap-4">
							<div className="w-full">
								<ExtraRoleInfo setNotification={setNotification} />
							</div>
						</div>
					)}
				</div>
			</div>

			{todayShown && hasWriteSchedulesPermission && (
				<PatientReassignmentConfirmation
					title="New patients were added to the schedule."
					message={`The following patient reassignments are suggested based on current availability:`}
					isOpen={isAddOnReassignmentOpen}
					onAccept={handleAddOnReassignmentAccept}
					onDecline={handleAddOnReassignmentDecline}
					remove={false}
					reassignments={reassignmentsForAddOnPatients}
					scheduleSelection={scheduleSelection}
				/>
			)}
			{todayShown && hasWriteSchedulesPermission && (
				<PatientReassignmentConfirmation
					title="Suggested re-assignments due to No Shows and Cancellations"
					message={`The following patient reassignments are suggested based on current availability:`}
					isOpen={isRemovedReassignmentOpen}
					onAccept={handleRemovedReassignmentAccept}
					onDecline={handleRemovedReassignmentDecline}
					remove={false}
					reassignments={reassignmentsForRemovedPatients}
					scheduleSelection={scheduleSelection}
				/>
			)}
			{todayShown && hasWriteSchedulesPermission && selectedTasks.length > 0 && (
				<EditPatient
					patient={selectedTasks[0]}
					isOpen
					onClose={handleCloseEditPatientPopup}
					schedule={currentSchedule}
					scheduleSelection={scheduleSelection}
					setNotification={setNotification}
					permissions={permissions}
					disableClose
				/>
			)}
			{todayShown && hasWriteSchedulesPermission && (
				<ReassignmentsErrorPopup
					error={reassignmentsError}
					onClose={() => {
						if (
							unassignedPatientsForAddOnPatients &&
							unassignedPatientsForAddOnPatients.length > 0
						) {
							setSelectedTasks([...unassignedPatientsForAddOnPatients])
						}
					}}
					unassignedPatientsForAddOnPatients={unassignedPatientsForAddOnPatients}
					setNotification={setNotification}
					logging={logging}
				/>
			)}

			<ReassignmentsErrorPopup
				error={reassignmentsError}
				onClose={false}
				unassignedPatientsForAddOnPatients={patientSchedule}
				setNotification={setNotification}
				logging={logging}
			/>

			{/* Add CancelledPatientsPopup component */}
			{todayShown && hasWriteSchedulesPermission && !permissions?.includes("write:admin") && (
				<CancelledPatientsPopup
					isOpen={isCancelledPatientsPopupOpen}
					onClose={() => onShowCancelledPatientsPopupClose()}
					cancelledPatients={notShownCancelledPatients}
					nurseSchedule={nurseSchedule}
					chairSchedule={chairSchedule}
				/>
			)}
		</>
	)
}

export default CustomCalendar
